'use client'

import { useMemo } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { useAppRouterLocale } from '@/app/[locale]/useAppRouterLocale'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { TextWithLink } from '@/components/TextWithLink'
import { useCarTrialExtensionQuery } from '@/services/graphql/generated'
import { CarTrialProduct } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { type SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { type QueryHookOptions } from '@apollo/client'
import { storyblokEditable } from '@storyblok/react'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { CarExtensionOffersProvider } from './CarTrialExtensionOffers/CarTrialExtensionOffers'
import { TrialExtensionOfferCard } from './CarTrialExtensionOffers/TrialExtensionOfferCard'
import { TrialExtensionUpsell } from './CarTrialExtensionOffers/TrialExtensionUpsell'
import { LoadingSkeleton } from './LoadingSkeleton/LoadingSkeleton'
import type { CarTrialExtension, TrialContract } from './carDealership.types'
import { CarTrialContractOfferCard } from './CarTrialContractOfferCard'
import { PayForTrial } from './PayForTrial'
import { TrialExtensionAddonSelector } from './TrialExtensionAddonSelector'

type Props = SbBaseBlockProps<{
  requirePaymentConnection?: boolean
}>

export const CarTrialExtensionBlock = (props: Props) => {
  const { t } = useTranslation('carDealership')
  const locale = useRoutingLocale()

  const data = useCarTrialQuery({})

  const trialContact: TrialContract | null = useMemo(() => {
    if (data?.carTrial) {
      return {
        ...data.carTrial.trialContract,
        pillowSrc: data.carTrial.priceIntent.product.pillowImage.src,
      }
    }

    return null
  }, [data])

  if (!data?.carTrial || !trialContact) {
    return (
      <GridLayout.Root>
        <GridLayout.Content width="1/3" align="center">
          <LoadingSkeleton />
        </GridLayout.Content>
      </GridLayout.Root>
    )
  }

  const trialDays = data.carTrial.product === CarTrialProduct.Hedvig_14Days ? '14' : '60'
  const requirePaymentConnection = props.blok.requirePaymentConnection
  const showExtensionDescription = !requirePaymentConnection

  return (
    <GridLayout.Root className={sprinkles({ rowGap: { _: 'xl', md: 'xxl' } })}>
      {showExtensionDescription && (
        <GridLayout.Content width={{ md: '5/6', lg: '1/2' }} align="center">
          <Text
            as="p"
            align="center"
            balance={true}
            className={sprinkles({ marginInline: 'auto' })}
            color="textSecondary"
            style={{ maxWidth: '32rem' }}
          >
            {t('CAR_TRIAL_EXTENSION_DESCRIPTION', { days: trialDays })}
          </Text>
        </GridLayout.Content>
      )}

      <GridLayout.Content width={{ md: '2/3', lg: '1/2', xl: '1/3' }} align="center">
        <div className={yStack({ gap: 'md' })}>
          <Text align="center" size="md">
            {t('YOUR_OFFER')}
          </Text>
          {requirePaymentConnection && <CarTrialContractOfferCard contract={trialContact} />}

          <CarExtensionOffersProvider
            priceIntent={data.carTrial.priceIntent}
            shopSession={data.carTrial.shopSession}
            requirePaymentConnection={props.blok.requirePaymentConnection ?? false}
            trialContract={trialContact}
          >
            <TrialExtensionOfferCard {...storyblokEditable(props.blok)} />

            <TrialExtensionAddonSelector />

            <TrialExtensionUpsell />

            <PayForTrial
              defaultOffer={data.carTrial.priceIntent.defaultOffer ?? undefined}
              trialContract={trialContact}
              shopSessionId={data.carTrial.shopSession.id}
              ssn={data.carTrial.shopSession.customer?.ssn ?? undefined}
            />
          </CarExtensionOffersProvider>
        </div>

        <div className={yStack({ gap: 'lg', mt: 'lg' })}>
          <div className={xStack({ alignItems: 'flex-start', gap: 'xs', justifyContent: 'center' })}>
            <CheckIcon size="1rem" />
            <Text size="xs">{t('USP_TEXT')}</Text>
          </div>

          <TextWithLink
            as="p"
            size="xs"
            align="center"
            balance={true}
            color="textSecondary"
            href={PageLink.privacyPolicy({ locale })}
            target="_blank"
          >
            {t('SIGN_DISCLAIMER', { ns: 'checkout' })}
          </TextWithLink>
        </div>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}

type UseCarTrialQueryParams = Pick<QueryHookOptions<NonNullable<CarTrialExtension>>, 'onCompleted'>

const useCarTrialQuery = (params: UseCarTrialQueryParams) => {
  const router = useRouter()
  const routeParams = useParams<{ contractId: string }>()
  const locale = useAppRouterLocale()

  const contractId = routeParams.contractId

  const { data } = useCarTrialExtensionQuery({
    variables: contractId ? { contractId } : undefined,
    skip: !contractId,
    onCompleted(data) {
      const { carTrial } = data
      if (!carTrial) return
      params.onCompleted?.(carTrial)
    },
    onError(error) {
      logger.warn('Car dealership | Failed to load trial data', {
        name: error.name,
        message: error.message,
      })
      router.push(PageLink.fourOhFour({ locale }).pathname)
    },
  })

  return data
}
